/**
 * Pure recording / upload state machine.
 *
 * Mirrors the control flow in useAudioRecordings without touching network,
 * IndexedDB, or MediaRecorder. Production hook switch is a separate step.
 *
 * Sharp edges encoded here:
 * - Finalize latch is cleared when finalize starts — a second tryFinalize
 *   while the request is in flight is a no-op; failure leaves nothing to
 *   retry except a manual retryRequested (which rebuilds from IDB).
 * - drained only advances when the upload was not dead-lettered; after a
 *   dead letter only manual retry moves the machine forward.
 * - retryReady recomputes totalChunks from the IDB chunk list length.
 */

export type UploadPhase =
    | "uploading"
    | "finalizing"
    | "uploaded"
    | "failed_finalize"
    | "failed_incomplete"
    | "resuming"
    | "recovering"

export type ActiveRecording = {
    sessionId: string
    profileId: string
    chunkCount: number
    pendingChunks: number
    deadLettered: boolean
    sessionClosed: boolean
    /** Server refused further audio; finalize only the chunks already stored. */
    dropPending?: boolean
}

export type UploadState = {
    sessionId: string
    profileId: string
    phase: UploadPhase
    totalChunks: number
    pendingChunks: number
    /** When true, drained / tryFinalize may start finalize. Cleared on start. */
    finalizeArmed: boolean
    deadLettered: boolean
    sessionClosed: boolean
}

export type RecordingState = {
    activeRecording: ActiveRecording | null
    /** Keyed by localId (or sessionId for anonymous recovery rows). */
    uploads: Record<string, UploadState>
}

export type RecordingEvent =
    | { type: "recordingStarted"; sessionId: string; profileId: string }
    | { type: "chunkReady" }
    | { type: "chunkUploaded"; localId?: string }
    | { type: "recordingStopped"; localId: string; totalChunks: number }
    | { type: "drained"; localId: string }
    | { type: "deadLetter"; localId?: string }
    | { type: "sessionClosed"; localId?: string }
    | { type: "quotaStop" }
    | { type: "finalizeSucceeded"; localId: string }
    | { type: "finalizeFailed"; localId: string }
    | { type: "retryRequested"; localId: string }
    | {
          type: "retryReady"
          localId: string
          /** Recomputed as chunks.length from IDB — not the original total. */
          totalChunks: number
          pendingChunks: number
      }
    | {
          type: "recoveryFound"
          localId: string
          sessionId: string
          profileId: string
          totalChunks: number
          pendingChunks: number
      }
    | { type: "recoveryPurged"; localId: string }
    | { type: "recoveryFinalized"; localId: string }

export const initialRecordingState: RecordingState = {
    activeRecording: null,
    uploads: {},
}

function tryFinalize(upload: UploadState): UploadState {
    if (!upload.finalizeArmed) return upload
    // Dead letter / session close: drained will not fire — fail now even if
    // pendingChunks > 0 (queue may still be emptying, but finalize is blocked).
    if (upload.deadLettered || upload.sessionClosed) {
        return {
            ...upload,
            finalizeArmed: false,
            phase: "failed_incomplete",
        }
    }
    if (upload.pendingChunks > 0) return upload
    // Latch cleared BEFORE async completeUpload — second attempt is a no-op.
    return {
        ...upload,
        finalizeArmed: false,
        phase: "finalizing",
    }
}

function patchUpload(
    state: RecordingState,
    localId: string,
    patch: (u: UploadState) => UploadState,
): RecordingState {
    const current = state.uploads[localId]
    if (!current) return state
    return {
        ...state,
        uploads: {
            ...state.uploads,
            [localId]: patch(current),
        },
    }
}

function removeUpload(state: RecordingState, localId: string): RecordingState {
    if (!(localId in state.uploads)) return state
    const rest = { ...state.uploads }
    delete rest[localId]
    return { ...state, uploads: rest }
}

function resolveActiveLocalId(state: RecordingState, localId?: string): string | undefined {
    if (localId && state.uploads[localId]) return localId
    const activeSessionId = state.activeRecording?.sessionId
    if (activeSessionId) {
        const match = Object.entries(state.uploads).find(
            ([, u]) => u.sessionId === activeSessionId,
        )
        if (match) return match[0]
    }
    return localId
}

export function recordingReducer(state: RecordingState, event: RecordingEvent): RecordingState {
    switch (event.type) {
        case "recordingStarted": {
            return {
                ...state,
                activeRecording: {
                    sessionId: event.sessionId,
                    profileId: event.profileId,
                    chunkCount: 0,
                    pendingChunks: 0,
                    deadLettered: false,
                    sessionClosed: false,
                },
            }
        }

        case "chunkReady": {
            const active = state.activeRecording
            if (!active) return state
            return {
                ...state,
                activeRecording: {
                    ...active,
                    chunkCount: active.chunkCount + 1,
                    pendingChunks: active.pendingChunks + 1,
                },
            }
        }

        case "chunkUploaded": {
            if (event.localId) {
                return patchUpload(state, event.localId, (u) => ({
                    ...u,
                    pendingChunks: Math.max(0, u.pendingChunks - 1),
                }))
            }
            const active = state.activeRecording
            if (!active) return state
            return {
                ...state,
                activeRecording: {
                    ...active,
                    pendingChunks: Math.max(0, active.pendingChunks - 1),
                },
            }
        }

        case "quotaStop": {
            const active = state.activeRecording
            if (!active) return state
            return {
                ...state,
                activeRecording: { ...active, dropPending: true },
            }
        }

        case "recordingStopped": {
            const active = state.activeRecording
            if (!active) return state
            const accepted = Math.max(0, active.chunkCount - active.pendingChunks)
            if (active.dropPending && accepted === 0) {
                return { ...state, activeRecording: null }
            }

            const upload: UploadState = {
                sessionId: active.sessionId,
                profileId: active.profileId,
                phase: "uploading",
                totalChunks: active.dropPending ? accepted : event.totalChunks,
                pendingChunks: active.dropPending ? 0 : active.pendingChunks,
                finalizeArmed: true,
                deadLettered: active.deadLettered,
                sessionClosed: active.sessionClosed,
            }

            return {
                activeRecording: null,
                uploads: {
                    ...state.uploads,
                    [event.localId]: tryFinalize(upload),
                },
            }
        }

        case "drained": {
            // Queue only emits drained when !deadLettered. Encode the same gate:
            // a drained after dead letter must not auto-finalize.
            return patchUpload(state, event.localId, (u) => {
                if (u.deadLettered || u.sessionClosed) return u
                return tryFinalize({ ...u, pendingChunks: 0 })
            })
        }

        case "deadLetter": {
            const active = state.activeRecording
            if (active && !event.localId) {
                return {
                    ...state,
                    activeRecording: { ...active, deadLettered: true },
                }
            }
            const localId = resolveActiveLocalId(state, event.localId)
            if (!localId || !state.uploads[localId]) {
                if (active) {
                    return {
                        ...state,
                        activeRecording: { ...active, deadLettered: true },
                    }
                }
                return state
            }
            return patchUpload(state, localId, (u) => ({
                ...u,
                deadLettered: true,
                finalizeArmed: false,
                phase: "failed_incomplete",
            }))
        }

        case "sessionClosed": {
            const active = state.activeRecording
            if (active && !event.localId) {
                return {
                    ...state,
                    activeRecording: { ...active, sessionClosed: true },
                }
            }
            const localId = resolveActiveLocalId(state, event.localId)
            if (!localId || !state.uploads[localId]) {
                if (active) {
                    return {
                        ...state,
                        activeRecording: { ...active, sessionClosed: true },
                    }
                }
                return state
            }
            return patchUpload(state, localId, (u) => ({
                ...u,
                sessionClosed: true,
                finalizeArmed: false,
                phase: "failed_incomplete",
            }))
        }

        case "finalizeSucceeded": {
            return patchUpload(state, event.localId, (u) => {
                if (u.phase !== "finalizing" && u.phase !== "recovering") return u
                return { ...u, phase: "uploaded", pendingChunks: 0 }
            })
        }

        case "finalizeFailed": {
            return patchUpload(state, event.localId, (u) => {
                if (u.phase !== "finalizing") return u
                // Latch already cleared when finalize started — nothing to re-arm.
                return { ...u, phase: "failed_finalize" }
            })
        }

        case "retryRequested": {
            return patchUpload(state, event.localId, (u) => {
                if (
                    u.phase !== "failed_finalize" &&
                    u.phase !== "failed_incomplete" &&
                    u.phase !== "uploading"
                ) {
                    return u
                }
                return {
                    ...u,
                    phase: "resuming",
                    finalizeArmed: false,
                    deadLettered: false,
                    sessionClosed: false,
                }
            })
        }

        case "retryReady": {
            return patchUpload(state, event.localId, (u) => {
                if (u.phase !== "resuming") return u
                return tryFinalize({
                    ...u,
                    // Retry recomputes total from IDB chunk list length.
                    totalChunks: event.totalChunks,
                    pendingChunks: event.pendingChunks,
                    finalizeArmed: true,
                    deadLettered: false,
                    sessionClosed: false,
                    phase: event.pendingChunks > 0 ? "uploading" : u.phase,
                })
            })
        }

        case "recoveryFound": {
            const upload: UploadState = {
                sessionId: event.sessionId,
                profileId: event.profileId,
                phase: "recovering",
                totalChunks: event.totalChunks,
                pendingChunks: event.pendingChunks,
                finalizeArmed: true,
                deadLettered: false,
                sessionClosed: false,
            }
            return {
                ...state,
                uploads: {
                    ...state.uploads,
                    [event.localId]:
                        event.pendingChunks === 0
                            ? tryFinalize(upload)
                            : upload,
                },
            }
        }

        case "recoveryPurged": {
            return removeUpload(state, event.localId)
        }

        case "recoveryFinalized": {
            return patchUpload(state, event.localId, (u) => {
                if (u.phase !== "finalizing" && u.phase !== "recovering") return u
                return { ...u, phase: "uploaded", pendingChunks: 0, finalizeArmed: false }
            })
        }

        default: {
            const _exhaustive: never = event
            return _exhaustive
        }
    }
}

/** Convenience: run a sequence of events (for tests and adapters). */
export function reduceRecording(
    state: RecordingState,
    events: RecordingEvent[],
): RecordingState {
    return events.reduce(recordingReducer, state)
}
